// MobileSheetWrapper — turns any existing desktop panel into a slide-up
// bottom sheet on phone screens, WITHOUT touching that panel's own JSX or
// logic.
//
// Usage: wrap an existing sidebar/panel block as-is:
//   <MobileSheetWrapper isMobile={isMobile} open={mobileSheet === 'stages'}
//     onClose={() => setMobileSheet(null)} title="Layout Stages">
//     {/* ...exactly the same JSX that already renders on desktop... */}
//   </MobileSheetWrapper>
//
// - On desktop (isMobile=false): renders children completely unwrapped —
//   a plain passthrough, so the desktop layout is byte-for-byte unchanged.
// - On mobile, closed: renders nothing (the panel takes no space, doesn't
//   fight the canvas for room).
// - On mobile, open: renders the SAME children inside a fixed, slide-up
//   sheet with a backdrop, drag handle, and title bar — closing on
//   backdrop tap or the close button. Because it's the exact same element
//   tree as desktop, every handler/state inside it keeps working exactly
//   as before; only the outer chrome changes.
import React from 'react';
import { X } from 'lucide-react';

export default function MobileSheetWrapper({ isMobile, open, onClose, title, children, maxHeight = '78vh', headerAction, backdrop = true }) {
  if (!isMobile) return children;
  if (!open) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 10200, pointerEvents: 'none' }}>
      {/* backdrop=false (e.g. the Compass sheet): skip the darkened overlay
          entirely so the canvas underneath — the compass rose, degree
          markings, floor plan — stays fully visible AND tappable while the
          rotation slider is being dragged. Dragging the slider needs to show
          its live effect on the canvas, which a full-screen backdrop hides. */}
      {backdrop && (
        <div
          onClick={onClose}
          style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(2px)', pointerEvents: 'auto' }}
        />
      )}
      <div
        style={{
          position: 'absolute', left: 0, right: 0, bottom: 0, maxHeight,
          background: '#fff', borderTopLeftRadius: '20px', borderTopRightRadius: '20px',
          boxShadow: '0 -8px 30px rgba(0,0,0,0.18)',
          display: 'flex', flexDirection: 'column',
          animation: 'mobileSheetSlideUp 220ms ease-out',
          pointerEvents: 'auto',
        }}
      >
        {/* Drag handle */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px', flexShrink: 0 }}>
          <div style={{ width: '38px', height: '4px', borderRadius: '999px', background: '#e2e8f0' }} />
        </div>

        {/* Title bar */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '4px 18px 12px', borderBottom: '1px solid #ECEEF2', flexShrink: 0,
        }}>
          <span style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1A1F36' }}>{title}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {headerAction}
            <button onClick={onClose} aria-label="Close"
              style={{
                width: 30, height: 30, borderRadius: '50%', border: 'none', background: '#F1F5F9',
                display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#334155',
              }}>
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Body — the exact same panel content, just scrollable inside the sheet now */}
        <div style={{ overflowY: 'auto', flex: 1, WebkitOverflowScrolling: 'touch' }}>
          {children}
        </div>
      </div>

      <style>{`
        @keyframes mobileSheetSlideUp {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
